/**
 * DisplayTokensManager — Panneau admin de gestion des écrans agence.
 *
 * Visible dans Paramètres agence, réservé aux propriétaires/administrateurs.
 * Permet de :
 *   - Générer un nouveau lien d'écran (nom de l'appareil)
 *   - Voir la liste des écrans actifs (expiration, dernier usage)
 *   - Copier le lien complet
 *   - Révoquer un token
 */
import { useEffect, useState } from "react";
import {
  Monitor, Plus, Copy, Trash2, Check, Loader2, Radio, ExternalLink,
  ClipboardCopy, X,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const fmtDate = (iso) => iso
  ? new Date(iso).toLocaleDateString("fr-FR", {
      day: "2-digit", month: "short", year: "numeric",
    })
  : "—";
const fmtDateTime = (iso) => iso
  ? new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    })
  : "jamais utilisé";

const absoluteDisplayUrl = (relOrAbs) => {
  if (!relOrAbs) return "";
  if (relOrAbs.startsWith("http")) return relOrAbs;
  return `${window.location.origin}${relOrAbs}`;
};

const DisplayTokensManager = () => {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");
  const [freshLink, setFreshLink] = useState(null);

  const load = async () => {
    try {
      const r = await api.listDisplayTokens();
      setTokens(r?.tokens || []);
    } catch (e) {
      toast?.error?.(e.message || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async (e) => {
    e?.preventDefault?.();
    if (!name.trim()) return;
    setCreating(true);
    try {
      const r = await api.createDisplayToken({ device_name: name.trim() });
      setFreshLink({
        url:  absoluteDisplayUrl(r.display_url),
        name: r.device_name,
        expires_at: r.expires_at,
      });
      setName("");
      setShowCreate(false);
      await load();
    } catch (e) {
      toast?.error?.(e.message || "Erreur");
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (t) => {
    if (!window.confirm(`Révoquer l'écran « ${t.device_name} » ?`)) return;
    try {
      await api.revokeDisplayToken(t.id);
      toast?.success?.("Écran révoqué");
      await load();
    } catch (e) {
      toast?.error?.(e.message || "Erreur");
    }
  };

  const copy = (text, msg = "Lien copié") => {
    navigator.clipboard.writeText(text).then(
      () => toast?.success?.(msg),
      () => toast?.error?.("Copie impossible")
    );
  };

  return (
    <div className="wyvio-card p-6 lg:p-7" data-testid="display-tokens-manager">
      <div className="flex items-start justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-[#111] text-[#C8FF2F] flex items-center justify-center">
            <Monitor size={16} strokeWidth={2} />
          </div>
          <div>
            <h3 className="text-[15px] font-semibold text-[#111] tracking-tight">
              Écrans agence
            </h3>
            <p className="text-[11.5px] text-[#6B7280]">
              Tokens sécurisés pour un TV/écran partagé (lecture seule, 30 jours)
            </p>
          </div>
        </div>
        <button
          type="button"
          data-testid="display-token-new"
          onClick={() => setShowCreate(true)}
          className="h-9 inline-flex items-center gap-1.5 rounded-full bg-[#111] text-white px-3.5 text-[12px] font-medium hover:bg-black transition"
        >
          <Plus size={13} strokeWidth={2} />
          Nouvel écran
        </button>
      </div>

      {loading ? (
        <p className="py-8 text-center text-[12px] text-[#9CA3AF]">Chargement…</p>
      ) : tokens.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#EEF2F5] p-8 text-center">
          <Radio size={20} strokeWidth={1.5} className="mx-auto text-[#9CA3AF]" />
          <p className="mt-3 text-[13px] text-[#111] font-semibold">
            Aucun écran configuré
          </p>
          <p className="mt-1 text-[12px] text-[#6B7280]">
            Générez un lien pour afficher le Command Center sur un TV partagé.
          </p>
        </div>
      ) : (
        <ul className="space-y-2">
          {tokens.map((t) => (
            <li
              key={t.id}
              data-testid={`display-token-row-${t.id}`}
              className={`rounded-2xl border p-4 flex items-center gap-3 ${t.revoked || t.expired ? "border-[#EEF2F5] bg-[#FAFBFC] opacity-70" : "border-[#EEF2F5] bg-white"}`}
            >
              <div className={`h-10 w-10 rounded-full flex items-center justify-center shrink-0 ${t.active ? "bg-[#F0FFDE] text-[#4a6b0f]" : "bg-[#F7F8FA] text-[#9CA3AF]"}`}>
                <Monitor size={16} strokeWidth={2} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-[13.5px] font-semibold text-[#111]">{t.device_name}</p>
                  {t.revoked && (
                    <span className="text-[10.5px] font-medium text-white bg-[#111111] rounded-full px-1.5 py-0.5">révoqué</span>
                  )}
                  {!t.revoked && t.expired && (
                    <span className="text-[10.5px] font-medium text-white bg-[#F59E0B] rounded-full px-1.5 py-0.5">expiré</span>
                  )}
                  {t.active && (
                    <span className="text-[10.5px] font-medium text-[#4a6b0f] bg-[#F0FFDE] rounded-full px-1.5 py-0.5">actif</span>
                  )}
                </div>
                <p className="mt-0.5 text-[11px] text-[#6B7280]">
                  Créé par {t.created_by || "?"} · Expire le {fmtDate(t.expires_at)}
                </p>
                <p className="text-[10.5px] text-[#9CA3AF]">
                  Dernière activité : {fmtDateTime(t.last_used_at)} · {t.usage_count} appels
                </p>
              </div>
              {!t.revoked && !t.expired && (
                <button
                  type="button"
                  data-testid={`display-token-revoke-${t.id}`}
                  onClick={() => handleRevoke(t)}
                  className="h-8 w-8 rounded-full inline-flex items-center justify-center text-[#6B7280] hover:text-[#111111] hover:bg-[#F7F8FA]"
                  title="Révoquer"
                >
                  <Trash2 size={13} strokeWidth={2} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Modal création */}
      {showCreate && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
              onClick={() => setShowCreate(false)}>
          <div className="wyvio-card w-full max-w-md p-6"
                onClick={(e) => e.stopPropagation()}
                data-testid="display-token-modal">
            <div className="flex items-start justify-between mb-3">
              <h3 className="text-[15.5px] font-semibold">Nouvel écran</h3>
              <button onClick={() => setShowCreate(false)}
                       className="h-8 w-8 rounded-full hover:bg-[#F7F8FA] flex items-center justify-center">
                <X size={14} />
              </button>
            </div>
            <form onSubmit={handleCreate} className="space-y-3">
              <div>
                <label className="text-[11.5px] font-medium uppercase tracking-wider text-[#6B7280]">
                  Nom de l&apos;appareil
                </label>
                <input
                  type="text"
                  value={name}
                  autoFocus
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ex: Écran showroom"
                  maxLength={80}
                  data-testid="display-token-name"
                  className="mt-2 w-full h-10 rounded-full border border-[#EEF2F5] bg-white px-4 text-[13px] text-[#111] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-[#C8FF2F]/60"
                />
              </div>
              <p className="text-[11px] text-[#9CA3AF]">
                Le lien reste valide 30 jours et peut être révoqué à tout moment.
              </p>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCreate(false)}
                  className="h-10 px-4 rounded-full text-[12.5px] font-medium text-[#6B7280] hover:text-[#111]"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={creating || !name.trim()}
                  data-testid="display-token-create"
                  className="h-10 px-5 rounded-full inline-flex items-center gap-1.5 bg-[#C8FF2F] text-[#111] text-[12.5px] font-semibold hover:brightness-95 transition disabled:opacity-50"
                >
                  {creating && <Loader2 size={13} className="animate-spin" />}
                  Générer le lien
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal fresh link (raw token affiché UNE SEULE FOIS) */}
      {freshLink && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
              onClick={() => setFreshLink(null)}>
          <div className="wyvio-card w-full max-w-lg p-6"
                onClick={(e) => e.stopPropagation()}
                data-testid="display-fresh-link-modal">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-full bg-[#F0FFDE] text-[#4a6b0f] flex items-center justify-center shrink-0">
                <Check size={16} strokeWidth={2.25} />
              </div>
              <div className="flex-1">
                <h3 className="text-[15.5px] font-semibold">
                  Lien d&apos;écran généré
                </h3>
                <p className="mt-1 text-[12px] text-[#6B7280]">
                  <b>{freshLink.name}</b> · expire le {fmtDate(freshLink.expires_at)}. Copiez ce lien
                  maintenant et ouvrez-le sur l&apos;écran partagé.
                </p>
              </div>
            </div>
            <div className="mt-4 rounded-2xl bg-[#F7F8FA] border border-[#EEF2F5] p-3 flex items-center gap-2">
              <code className="text-[11.5px] flex-1 min-w-0 truncate font-mono text-[#111]" data-testid="display-fresh-link-url">
                {freshLink.url}
              </code>
              <button
                type="button"
                onClick={() => copy(freshLink.url)}
                data-testid="display-fresh-link-copy"
                className="h-8 px-3 inline-flex items-center gap-1.5 rounded-full bg-[#111] text-white text-[11.5px] font-medium hover:bg-black"
              >
                <ClipboardCopy size={12} /> Copier
              </button>
              <a
                href={freshLink.url}
                target="_blank"
                rel="noreferrer"
                className="h-8 px-3 inline-flex items-center gap-1.5 rounded-full border border-[#EEF2F5] text-[11.5px] font-medium text-[#111] hover:bg-[#F7F8FA]"
              >
                <ExternalLink size={12} /> Ouvrir
              </a>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={() => setFreshLink(null)}
                className="h-9 px-4 rounded-full text-[12.5px] font-medium text-[#6B7280] hover:text-[#111]"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DisplayTokensManager;
